import "./CreateDBTRule.scss";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RiDeleteBin3Line } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { notify } from '../../services/notification';
import { IoMdInformationCircleOutline } from "react-icons/io";

import DetailsModal from "../DbtRuleMangement/dbtDetailsModal";
import Header from "../../component/loaderComponent/Header/Header";
import CreateDBTSkeleton from "../../component/SkeletonLoader/CreateDBTSkeleton";
import MultiSelectDropdown from "../../component/MultiSelctDropdown/MultiSelectDropdown";
import { createDbtRuleAction, deleteDbtRuleAction, getDbtRuleDetailAction, updateDbtRuleAction, getClusterListAction, getClusterPartListAction, getRegionSearchAction } from "../../actions/dbtAction";
import SearchableDropdown from "../../component/SearchableDropdown/SearchableDropdown";

function CreateDBTRule() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { editId, viewId } = useParams();

    // ----- Mode flags -----
    const isEdit = Boolean(editId);
    const isView = Boolean(viewId);
    const isCreate = !isEdit && !isView;

    // ----- UI meta -----
    const [loading, setLoading] = useState(false);
    const title = isEdit ? "Edit DBT Rule" : isView ? "View DBT Rule" : "Create New DBT Rule";


    // ----- Form state (top section) -----
    const [form, setForm] = useState({
        dbtId: "", // usually auto-generated; still keep as state for controlled input
        name: "",
        description: "",
        effective_from: "",
        effective_to: "",
        regionType: "panIndia",
        regionSelections: [], // Array of selected region values
    });

    // ----- Line Items -----
    const [lines, setLines] = useState([
        {
            id: "001",
            is_cluster: true, // boolean: true for cluster, false for non-cluster
            name: "", // part/category or manual part no based on cluster/non-cluster
            incentive_percentage: "0",
        },
    ]);
    const [selectedItems, setSelectedItems] = useState([]);

    // ----- Cluster State -----
    const [clusterOptions, setClusterOptions] = useState([]); // Available cluster names from API
    const [selectedClusterParts, setSelectedClusterParts] = useState([]); // Parts for selected cluster (modal)
    const [selectedClusterName, setSelectedClusterName] = useState(''); // Currently selected cluster for modal
    const [loadingClusterParts, setLoadingClusterParts] = useState(false); // Loading state for cluster parts

    // ----- Region Search State -----
    const [regionOptions, setRegionOptions] = useState([]); // Available region options from API
    const [loadingRegions, setLoadingRegions] = useState(false); // Loading state for region search

    // ----- Modal State -----
    const [detailRowOpen, setDetailRowOpen] = useState(false);
    const closeDetail = () => setDetailRowOpen(false);
    // console.log(form, lines);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split("T")[0];
    // ----- Disable rules -----
    // Everything disabled in View mode
    const disabledAll = isView;
    // Only effective dates editable in Edit mode; non-date fields disabled in Edit & View
    const disabledNonDates = isView || isEdit;
    // Convenience helpers
    const canEditEffectiveDates = isCreate || isEdit; // true for create & edit (only these dates can be edited in edit mode)
    const canMutateLines = isCreate; // add/delete/change only in create
    const canChangeRegion = isCreate; // region type & selections only in create
    const canChangeBasics = isCreate; // dbtId/name/description only in create

    // ----- Part Validation Helper -----
    const isPartComplete = (part) => {
        // Check if cluster/non-cluster is selected (not empty string)
        const hasType = part.is_cluster === true || part.is_cluster === false;

        // Check if name is filled
        const hasName = part.name && part.name.trim() !== "";

        // Check if incentive percentage is filled and valid
        const hasPercentage = part.incentive_percentage &&
                              part.incentive_percentage.trim() !== "" &&
                              parseFloat(part.incentive_percentage) > 0;

        return hasType && hasName && hasPercentage;
    };

    // ----- Form Validation -----
    const isFormValid = useMemo(() => {
        // In Edit mode, only effective dates are required
        if (isEdit) {
            return form.effective_from && form.effective_to;
        }

        // In Create mode, validate all mandatory fields
        if (isCreate) {
            // Basic fields validation
            const hasName = form.name.trim() !== "";
            const hasEffectiveFrom = form.effective_from !== "";
            const hasEffectiveTo = form.effective_to !== "";

            // Region validation - if not Pan India, must have selections
            const hasRegionSelection = form.regionType === "panIndia" || selectedItems.length > 0;

            // Parts validation
            const completeParts = lines.filter(isPartComplete);
            const hasValidParts = lines.length === 1
                ? completeParts.length === 1  // Single part must be complete
                : completeParts.length > 0;    // Multiple parts: at least one complete

            return hasName && hasEffectiveFrom && hasEffectiveTo && hasRegionSelection && hasValidParts;
        }

        return true; // View mode - always valid (save button will be disabled anyway)
    }, [isEdit, isCreate, form.name, form.effective_from, form.effective_to, form.regionType, selectedItems.length, lines]);

    // ----- Handlers: top-level fields -----
    const handleChange = (key) => (e) => {
        const value = e?.target ? e.target.value : e;

        // Special handling for effective_from date changes
        if (key === "effective_from" && value) {
            setForm((prev) => {
                const newStartDate = new Date(value);
                const currentEndDate = prev.effective_to ? new Date(prev.effective_to) : null;

                // If end date exists and is now invalid (not after start date), clear it
                if (currentEndDate && currentEndDate <= newStartDate) {
                    notify.warning("Effective End Date cleared because it must be after the Start Date");
                    return {
                        ...prev,
                        effective_from: value,
                        effective_to: "" // Clear invalid end date
                    };
                }

                return { ...prev, [key]: value };
            });
        } else {
            setForm((prev) => ({ ...prev, [key]: value }));
        }
    };

    // Region type radio
    const handleRegionTypeChange = (e) => {
        if (!canChangeRegion) return;
        const next = e.target.value;
        setForm((prev) => ({
            ...prev,
            regionType: next,
            // Clear selections when switching region type (optional but safer)
            regionSelections: next === "panIndia" ? [] : prev.regionSelections,
        }));
        // Clear region options and selected items when region type changes
        setRegionOptions([]);
        setSelectedItems([]);
    };

    // MultiSelectDropdown: expecting onChange(itemsArray) -> keep as-is; adapt if your component differs
    const handleRegionSelectionsChange = (valuesArray) => {
        if (!canChangeRegion) return;

        setForm((prev) => ({ ...prev, regionSelections: Array.isArray(valuesArray) ? valuesArray : [] }));
    };

    // Handle region search for dynamic dropdown - wrapped with useCallback to prevent re-creation
    const handleRegionSearch = useCallback(async (searchValue) => {
        if (!canChangeRegion) return;

        // Clear options if search is empty
        if (!searchValue || searchValue.length === 0) {
            setRegionOptions([]);
            return;
        }

        // API will handle minimum 3 character validation
        if (searchValue.length < 3) {
            setRegionOptions([]);
            return;
        }

        try {
            setLoadingRegions(true);
            const response = await dispatch(getRegionSearchAction(form.regionType, searchValue));

            if (response?.status === 200 && response?.data?.success) {
                const regions = response?.data?.data || [];
                setRegionOptions(regions);
            } else {
                setRegionOptions([]);
            }
        } catch (error) {
            console.error('Error searching regions:', error);
            setRegionOptions([]);
        } finally {
            setLoadingRegions(false);
        }
    }, [canChangeRegion, dispatch, form.regionType]);

    // Load all clusters on mount
    const loadAllClusters = async () => {
        try {
            const response = await dispatch(getClusterListAction());
            if (response?.status === 200 && response?.data?.success) {
                const clusters = response?.data?.data || [];
                setClusterOptions(clusters);
            } else {
                setClusterOptions([]);
            }
        } catch (error) {
            console.error('Error loading clusters:', error);
            setClusterOptions([]);
        }
    };

    // ----- Line Items handlers -----
    const handleAddLine = () => {
        if (!canMutateLines) return;
        setLines((prev) => [
            ...prev,
            {
                id: String(prev.length + 1).padStart(3, "0"),
                is_cluster: true,
                name: "",
                incentive_percentage: "0",
            },
        ]);
    };

    const handleDeleteLine = async (idx) => {
        if (!canMutateLines) return;
        setLines((prev) => prev.filter((_, i) => i !== idx));
        if (isView) return;

        const payload = {
            ...form,
            lines,
            mode: isCreate ? "create" : isEdit ? "edit" : "view",
            // You can attach editId when editing
            editId: isEdit ? editId : undefined,
        };
        // try {
        //     setLoading(true);
        //     const response = await dispatch(deleteDbtRuleAction(editId));
        //     if (response?.status === 200) {
        //         setLoading(false);
        //         notify.success(response?.data?.message || 'DBT Rule deleted successfully');
        //         navigate("/dbtrulemanagement");
        //     } else if (response?.status === 400) {
        //         setLoading(false);
        //         notify.error(response?.data?.message);
        //     } else {
        //         setLoading(false);
        //         notify.error(response?.data?.message || 'Update failed');
        //     }
        // } catch (error) {
        //     setLoading(false);
        //     notify.error(error?.data?.message || 'An error occurred');
        // }
    };

    const handleLineChange = (idx, key, value) => {
        setLines((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [key]: value };
            return next;
        });
    };

    const handleClusterToggle = (idx) => (e) => {
        if (!canMutateLines) return;
        const val = e.target.value; // "true" | "false" as string
        const isCluster = val === "true";
        setLines((prev) => {
            const next = [...prev];
            // Reset name when toggling to avoid stale value
            next[idx] = { ...next[idx], is_cluster: isCluster, name: "" };
            return next;
        });
    };

    const handlePartValueChange = (idx) => (e) => {
      if (!canMutateLines) return;

      const rawValue = e.target.value;

      // Allow only alphanumeric (letters + numbers)
      const isAlphanumeric = /^[a-zA-Z0-9]*$/.test(rawValue);

      if (isAlphanumeric) {
        handleLineChange(idx, "name", rawValue);
      }
    };


    // const handlePartValueChange = (idx) => (e) => {
    //     if (!canMutateLines) return;
    //     handleLineChange(idx, "name", e.target.value);
    // };

    // const handlePercentChange = (idx) => (e) => {
    //     if (!canMutateLines) return;
    //     const raw = e.target.value;
    //     if (!/^\d*\.?\d*$/.test(raw)) {
    //     e.target.value = raw.slice(0, -1); // remove last invalid character
    //     }
    //     // Keep as string to match API format
    //     const val = raw === "" ? "" : raw;
    //     handleLineChange(idx, "incentive_percentage", val);
    // };
    function preventInvalidKeys(e) {
        if (["e", "E", "+", "-"].includes(e.key)) {
        e.preventDefault();
        }
    }
    const handlePercentChange = (idx) => (e) => {
    if (!canMutateLines) return;
    
    const raw = e.target.value;
    
    // Allow empty string
    if (raw === "") {
      handleLineChange(idx, "incentive_percentage", "");
      return;
    }
    
    // Validate: only numbers and single decimal point (strict regex)
    // This prevents 'e', 'E', '+', '-', and any other characters
    if (!/^[0-9]*\.?[0-9]*$/.test(raw)) {
      return; // Ignore invalid input
    }
    
    // Prevent just a decimal point
    if (raw === ".") {
      return;
    }
    
    // Prevent multiple decimal points
    if ((raw.match(/\./g) || []).length > 1) {
      return;
    }
    
    handleLineChange(idx, "incentive_percentage", raw);
  };

    // ----- Actions -----
    const handleOpenDetails = async (clusterName) => {
        if (!clusterName) {
            notify.error('Please select a cluster first');
            return;
        }

        try {
            setLoadingClusterParts(true);
            setSelectedClusterName(clusterName);
            const response = await dispatch(getClusterPartListAction(clusterName));

            if (response?.status === 200 && response?.data?.success) {
                const parts = response?.data?.data || [];
                // Map API response to modal format
                const mappedParts = parts.map((part, index) => ({
                    id: part.part_no || index,
                    name: part.cluster || clusterName,
                    part_number: part.part_no || '',
                    description: part.part_desc || ''
                }));
                setSelectedClusterParts(mappedParts);
                setDetailRowOpen(true);
            } else {
                notify.error('Failed to load cluster parts');
            }
        } catch (error) {
            console.error('Error fetching cluster parts:', error);
            notify.error('Failed to load cluster parts');
        } finally {
            setLoadingClusterParts(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (isView) return;

        // Build payload based on mode
        let payload;

        if (isEdit) {
            // Edit mode: Only send effective dates
            payload = {
                effective_from: form.effective_from,
                effective_to: form.effective_to
            };
        } else {
            // Create mode: Send all fields
            // Filter to only include complete parts
            const completeParts = lines.filter(isPartComplete);

            payload = {
                name: form.name,
                description: form.description,
                effective_from: form.effective_from,
                effective_to: form.effective_to,
                is_pan_india: form.regionType === "panIndia",
                // Only include region_mapping if not Pan India
                ...(form.regionType !== "panIndia" && {
                    region_mapping: selectedItems.map(value => {
                        // For APS, extract code from compound key (format: "code|||name")
                        const actualValue = form.regionType === 'aps'
                            ? value.split('|||')[0]
                            : value;

                        return {
                            region_key: form.regionType,
                            region_value: actualValue
                        };
                    })
                }),
                // Map only complete parts to part_mapping with correct structure
                part_mapping: completeParts.map(line => ({
                    name: line.name.trim(), // Ensure no whitespace
                    is_cluster: line.is_cluster,
                    incentive_percentage: line.incentive_percentage
                }))
            };

            // Notify user if incomplete parts were skipped
            if (lines.length > completeParts.length) {
                const skippedCount = lines.length - completeParts.length;
                notify.info(
                    `${skippedCount} incomplete part${skippedCount > 1 ? 's' : ''} not included in submission`
                );
            }
        }


        try {
            setLoading(true);
            var response;
            if (isEdit) {
                response = await dispatch(updateDbtRuleAction(editId, payload));
            }
            if (isCreate) {
                response = await dispatch(createDbtRuleAction(payload));
            }
            if (response?.status === 200 || response?.status === 201) {
                setLoading(false);
                // notify.success(response?.data?.message || 'DBT Rule saved successfully');
                navigate("/dbtrulemanagement");
            } else if (response?.status === 400) {
                // Validation errors are already handled and displayed by the action
                // No need to show duplicate notification here
                setLoading(false);
            } else {
                setLoading(false);
                notify.error(response?.data?.message || 'Operation failed');
            }
        } catch (error) {
            setLoading(false);
            notify.error(error?.data?.message || 'An error occurred');
        }
    };

    const handleClose = () => {
        navigate("/dbtrulemanagement");
    };

    const fetchDBTRuleDetails = async () => {
        if (isCreate) return;
        try {
            setLoading(true);
            const response = await dispatch(getDbtRuleDetailAction(isEdit ? editId : viewId))
            if (response?.status === 200) {
                const dbtData = response?.data?.data;

                // Determine region type from the response
                let regionType = "panIndia";
                let regionSelections = [];

                // Check mapping_criteria (pan_india or specific region type)
                if (dbtData.mapping_criteria && dbtData.mapping_criteria !== "pan_india") {
                    regionType = dbtData.mapping_criteria;
                }

                // Extract region selections from region_mapping array
                if (dbtData.region_mapping && dbtData.region_mapping.length > 0) {
                    // Extract region type from first mapping if not already set
                    if (regionType === "panIndia" && dbtData.region_mapping[0].region_key) {
                        regionType = dbtData.region_mapping[0].region_key;
                    }
                    // Extract all region values
                    regionSelections = dbtData.region_mapping.map(item => item.region_value);
                }

                // Set form data
                setForm({
                    dbtId: dbtData.id || "",
                    name: dbtData.name || "",
                    description: dbtData.description || "",
                    effective_from: dbtData.effective_from || "",
                    effective_to: dbtData.effective_to || "",
                    regionType: regionType,
                    regionSelections: regionSelections,
                });

                // Set selected items for multiselect
                setSelectedItems(regionSelections);

                // Populate regionOptions with the selected values so they can be displayed in edit/view mode
                // For zone type, the MultiSelectDropdown uses static zones, so no need to set options
                // For other types (state/aps/pincode), we need to populate the options
                if (regionType !== "panIndia" && regionSelections.length > 0) {
                    setRegionOptions(regionSelections);
                }

                // Set line items (part_mapping)
                if (dbtData.part_mapping && dbtData.part_mapping.length > 0) {
                    const mappedLines = dbtData.part_mapping.map((part, index) => ({
                        id: String(index + 1).padStart(3, "0"),
                        name: part.name || "",
                        is_cluster: part.is_cluster === true,
                        incentive_percentage: String(part.incentive_percentage || "0"),
                    }));
                    setLines(mappedLines);
                }

                setLoading(false);
            } else if (response === undefined) {
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching DBT rule details:", error);
            notify.error("Failed to load DBT rule details");
            setLoading(false);
        }
    }


    useEffect(() => {
        fetchDBTRuleDetails();
    }, [isEdit, isView, editId, viewId]);

    useEffect(() => {
        loadAllClusters();
    }, []);

    return (
        <div className="create-dbt-container">
            <div className="create-dbt-containerright">
                <Header title={title} />

                {loading ? (
                    <CreateDBTSkeleton />
                ) : (
                    <form className="dbt-form" onSubmit={handleSave}>
                        {/* Basic Information */}
                        <div className="basic-form">
                            <h4>Basic Information</h4>

                            <div className="form-input-row">
                                {!isCreate && (
                                    <div className="form-label-input">
                                        <label htmlFor="dbtId">DBT ID</label>
                                        <input
                                            id="dbtId"
                                            type="text"
                                            placeholder="Auto Generated"
                                            className="input-field"
                                            value={form.dbtId}
                                            onChange={handleChange("dbtId")}
                                            disabled={!canChangeBasics}
                                        />
                                    </div>
                                )}
                                <div className="form-label-input">
                                    <label htmlFor="dbtName">
                                        DBT Name <span aria-hidden style={{ color: 'red' }}>&#42;</span>
                                    </label>
                                    <input
                                        id="dbtName"
                                        type="text"
                                        placeholder="Enter DBT Name"
                                        className="input-field"
                                        value={form.name}
                                        onChange={handleChange("name")}
                                        disabled={!canChangeBasics}
                                    />
                                    {/* {canChangeBasics && <span className="alert-label">This field is required</span>} */}
                                </div>
                            </div>

                            <div className="form-input-row">
                                <div className="form-label-input">
                                    <label htmlFor="dbtDesc">Description</label>
                                    <textarea
                                        id="dbtDesc"
                                        placeholder="Enter description"
                                        className="input-field"
                                        value={form.description}
                                        onChange={handleChange("description")}
                                        disabled={!canChangeBasics}
                                    />
                                </div>
                            </div>

                            <div className="form-input-row">
                                <div className="form-label-input">
                                    <label htmlFor="invStart">
                                        Effective Start Date <span aria-hidden style={{ color: 'red' }}>&#42;</span>
                                    </label>
                                    <input
                                        id="invStart"
                                        type="date"
                                        className="input-field"
                                        value={form.effective_from}
                                        onChange={handleChange("effective_from")}
                                        min={minDate} 
                                        disabled={!canEditEffectiveDates || disabledAll}
                                    />
                                </div>

                                <div className="form-label-input">
                                    <label htmlFor="invEnd">
                                        Effective End Date <span aria-hidden style={{ color: 'red' }}>&#42;</span>
                                    </label>
                                    <input
                                        id="invEnd"
                                        type="date"
                                        className="input-field"
                                        value={form.effective_to}
                                        // max={new Date().toISOString().split("T")[0]} 
                                          min={(() => {
                                            if (!form.effective_from) return undefined; // fallback if no date
                                            const date = new Date(form.effective_from);
                                            date.setDate(date.getDate() + 1);
                                            return date.toISOString().split("T")[0];
                                            })()}
                                        onChange={handleChange("effective_to")}
                                        disabled={!canEditEffectiveDates || disabledAll}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Region */}
                        <div className="basic-form">
                            <h4>Select Region</h4>

                            <div className="form-input-row">
                                <div className="form-label-input">
                                    <label>
                                        Region Criteria Type <span aria-hidden style={{ color: 'red' }}>&#42;</span>
                                    </label>

                                    <div className="radio-row">
                                        <div className="radio-group">
                                            <input
                                                id="panIndia"
                                                type="radio"
                                                value="panIndia"
                                                name="regionType"
                                                className="input-radio"
                                                checked={form.regionType === "panIndia"}
                                                onChange={handleRegionTypeChange}
                                                disabled={!canChangeRegion}
                                            />
                                            <label htmlFor="panIndia">Pan India</label>
                                        </div>
                                        <div className="radio-group">
                                            <input
                                                id="zone"
                                                type="radio"
                                                value="zone"
                                                name="regionType"
                                                className="input-radio"
                                                checked={form.regionType === "zone"}
                                                onChange={handleRegionTypeChange}
                                                disabled={!canChangeRegion}
                                            />
                                            <label htmlFor="zone">Zone</label>
                                        </div>
                                        <div className="radio-group">
                                            <input
                                                id="state"
                                                type="radio"
                                                value="state"
                                                name="regionType"
                                                className="input-radio"
                                                checked={form.regionType === "state"}
                                                onChange={handleRegionTypeChange}
                                                disabled={!canChangeRegion}
                                            />
                                            <label htmlFor="state">State</label>
                                        </div>
                                        <div className="radio-group">
                                            <input
                                                id="aps"
                                                type="radio"
                                                value="aps"
                                                name="regionType"
                                                className="input-radio"
                                                checked={form.regionType === "aps"}
                                                onChange={handleRegionTypeChange}
                                                disabled={!canChangeRegion}
                                            />
                                            <label htmlFor="aps">APS</label>
                                        </div>
                                        <div className="radio-group">
                                            <input
                                                id="pincode"
                                                type="radio"
                                                value="pincode"
                                                name="regionType"
                                                className="input-radio"
                                                checked={form.regionType === "pincode"}
                                                onChange={handleRegionTypeChange}
                                                disabled={!canChangeRegion}
                                            />
                                            <label htmlFor="pincode">Pincode</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {form.regionType !== "panIndia" && (
                                <div className="select-input-row">
                                    <div className="form-label-input">
                                        <label>
                                            Select <span aria-hidden style={{ color: 'red' }}>&#42;</span>
                                        </label>
                                        <MultiSelectDropdown
                                            selectedItems={selectedItems}
                                            setSelectedItems={setSelectedItems}
                                            regionType={form.regionType}
                                            options={regionOptions}
                                            loading={loadingRegions}
                                            onSearchChange={handleRegionSearch}
                                            disabled={!canChangeRegion}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Parts */}
                        <div className="add-parts">
                            <div className="add-parts-header">
                                <h4>Add Parts (Line Items)</h4>
                                <button
                                    className="add-parts-btn"
                                    type="button"
                                    onClick={handleAddLine}
                                    disabled={!canMutateLines}
                                    aria-disabled={!canMutateLines}
                                    title={!canMutateLines ? "You can add items only while creating a rule" : "Add Line Item"}
                                >
                                    + Add Line Item
                                </button>
                            </div>

                            <div className="parts-table">
                                <table className="part_table">
                                    <thead>
                                        <tr className="table-head-tr">
                                            <th className="th">Line ID</th>
                                            <th className="th">Cluster/Non-Cluster</th>
                                            <th className="th">Part Details</th>
                                            <th className="th">DBT %</th>
                                            <th className="th">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((item, index) => {
                                            const isCluster = item.is_cluster === true;
                                            return (
                                                <tr key={item.id}>
                                                    <td className="td">{item.id}</td>

                                                    <td>
                                                        <select
                                                            className="select-field"
                                                            value={String(item.is_cluster)}
                                                            onChange={handleClusterToggle(index)}
                                                            disabled={!canMutateLines}
                                                        >
                                                            <option value="">Select Option</option>
                                                            <option value="true">Cluster</option>
                                                            <option value="false">Non-Cluster</option>
                                                        </select>
                                                    </td>

                                                    <td>
                                                        {isCluster ? (
                                                            <div className="part-number-info">
                                                                <div className="cluster-dropdown-container" style={{ position: 'relative', width: '12rem' }}>
                                                                    <SearchableDropdown
                                                                        clusterListProp={clusterOptions}
                                                                        value={item.name}
                                                                        onChange={(cluster) => handleLineChange(index, "name", cluster)}
                                                                        disabled={!canMutateLines}
                                                                        placeholder="Select cluster"
                                                                    />
                                                                </div>
                                                                <IoMdInformationCircleOutline
                                                                    className="info-icon"
                                                                    onClick={() => handleOpenDetails(item.name)}
                                                                    title="View cluster details"
                                                                    style={{ cursor: item.name ? 'pointer' : 'not-allowed', opacity: item.name ? 1 : 0.5 }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                placeholder="Enter Part Number"
                                                                className="input-field"
                                                                value={item.name}
                                                                onChange={handlePartValueChange(index)}
                                                                disabled={!canMutateLines}
                                                            />
                                                        )}
                                                    </td>

                                                    <td>
                                                        <input     
                                                            type="number"
                                                            className="input-field"
                                                            value={item.incentive_percentage || ""} // Show empty if no value
                                                            onChange={handlePercentChange(index)}
                                                            onkeydown={(event) => preventInvalidKeys(event)}
                                                            disabled={!canMutateLines}
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="Enter percentage"
                                                        />
                                                    </td>

                                                    <td>
                                                        {canMutateLines && (
                                                            <button
                                                                className="delete-btn"
                                                                type="button"
                                                                onClick={() => handleDeleteLine(index)}
                                                                disabled={!canMutateLines}
                                                                aria-disabled={!canMutateLines}
                                                                title={canMutateLines ? "Delete line" : "Delete only in create mode"}
                                                            >
                                                                <RiDeleteBin3Line />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="btn-notify">
                            <button onClick={handleClose} className="dsr-active" type="button">
                                Close
                            </button>
                            <button
                                className="dsr-active"
                                type="submit"
                                disabled={isView || !isFormValid}
                                title={
                                    isView
                                        ? "Save disabled in View mode"
                                        : !isFormValid
                                            ? "Please fill all required fields"
                                            : "Save"
                                }
                            >
                                Save
                            </button>
                        </div>
                    </form>
                )}

                {detailRowOpen && (
                    <DetailsModal
                        detailRow={selectedClusterParts}
                        closeDetail={closeDetail}
                        clusterName={selectedClusterName}
                        loading={loadingClusterParts}
                    />
                )}
            </div>
        </div>
    );
}

export default CreateDBTRule;
